'use client'

import { TerminalIcon } from '@navikt/aksel-icons'
import { BodyShort, Button, Heading, List } from '@navikt/ds-react'
import { PageBlock } from '@navikt/ds-react/Page'
import React, { ReactElement } from 'react'

import { SessionIdInfo } from '#components/help/SessionIdInfo'
import { isDevGcp } from '#lib/env'

export function NoPractitionerSession(): ReactElement {
    return (
        <PageBlock as="main" width="xl" gutters className="pt-4">
            <div className="max-w-prose">
                <Heading size="large" spacing>
                    Du har blitt logget ut
                </Heading>
                <BodyShort spacing>Du har blitt logget ut av sykmeldingsløsningen for denne pasienten.</BodyShort>
                <BodyShort spacing>
                    Du kan prøve å laste siden på nytt, dersom dette ikke fungerer, må du må gjenåpne applikasjonen fra
                    ditt journalsystem.
                </BodyShort>
                <div className="flex gap-3 justify-end mt-8">
                    <Button
                        data-color="neutral"
                        type="button"
                        variant="secondary"
                        onClick={() => window.location.reload()}
                    >
                        Last siden på nytt
                    </Button>
                </div>
            </div>
            {isDevGcp && (
                <DevGcpErrorInfo why="Dette kan være at man har blitt logget ut, men det kan også være krasj/krøll i FHIR serveren." />
            )}
        </PageBlock>
    )
}

export function NoHelseIdInFhirSession(): ReactElement {
    return (
        <PageBlock as="main" width="xl" gutters className="pt-4">
            <div className="max-w-prose">
                <Heading size="large" spacing>
                    HelseID-sesjonen din er ikke gyldig
                </Heading>
                <BodyShort spacing>
                    Autorisasjon mot EPJ-systemet gikk greit, men din innlogging mot HelseID var ikke komplett.
                </BodyShort>
                <BodyShort spacing>
                    Du kan prøve å laste siden på nytt, dersom dette ikke fungerer, må du må gjenåpne applikasjonen fra
                    ditt journalsystem.
                </BodyShort>
                <div className="flex gap-3 justify-end mt-8">
                    <Button
                        data-color="neutral"
                        type="button"
                        variant="secondary"
                        onClick={() => window.location.reload()}
                    >
                        Last siden på nytt
                    </Button>
                </div>
            </div>
            {isDevGcp && <DevGcpErrorInfo why="SMART launch gikk greit, men HelseID tokenet er null." />}
        </PageBlock>
    )
}

export function NoValidPatient(): ReactElement {
    return (
        <PageBlock as="main" width="xl" gutters className="pt-4">
            <div className="max-w-prose">
                <Heading size="large" spacing>
                    Fant ingen (gyldig) pasient
                </Heading>
                <BodyShort spacing>
                    Dette tyder på at pasienten ikke finnes i, eller at pasienten ikke har en gyldig norsk identifikator
                    i ditt journalsystem (FNR eller DNR).
                </BodyShort>
            </div>
            {isDevGcp && (
                <DevGcpErrorInfo why="Dette betyr at FHIR API-et klarte ikke å gi oss en gyldig pasient. Mest sannsynligvis en intern feil hos EPJ, eventuelt ugyldig struktur (mindre sannsynlig)." />
            )}
        </PageBlock>
    )
}

function DevGcpErrorInfo({ why }: { why: string }): ReactElement | null {
    if (!isDevGcp) return null

    return (
        <div className="max-w-prose mt-8 bg-ax-bg-default p-4 rounded-xl">
            <Heading level="3" size="small" spacing className="flex flex-row gap-3 items-center -mt-1">
                <TerminalIcon aria-hidden />
                Teknisk debug for dev-gcp
            </Heading>
            <BodyShort spacing>{why}</BodyShort>
            <BodyShort spacing>
                For å se nøyaktig hva som gikk galt, kan du enten sjekke loggene eller OTEL-tracing. Lenkene under må{' '}
                <span className="font-ax-bold">KOPIERES</span> og åpnes på din faktiske maskin, det er ikke vits å åpne
                den i WinVerify1.
            </BodyShort>
            <List className="py-4">
                <List.Item>
                    <span>Logger: </span>
                    <a href="https://grafana.nav.cloud.nais.io/goto/QYbT-ZSvg?orgId=1">Kopier meg</a>
                    <span> (syk-inn dashboard i dev)</span>
                </List.Item>
                <List.Item>
                    <span>Traces: </span>
                    <a href="https://grafana.nav.cloud.nais.io/goto/GcvoaZSDg?orgId=1">Kopier meg</a>
                    <span> (error traces de siste 60 minuttene)</span>
                </List.Item>
            </List>
            <SessionIdInfo>
                <BodyShort>(Mest sannsynligvis ikke relevant for denne feilen)</BodyShort>
            </SessionIdInfo>
        </div>
    )
}
